function toNumber(value) {
    if (value == null || value === '') {
        return null;
    }
    const number = Number(value);
    return Number.isNaN(number) ? null : number;
}

/**
 * Compare actual order amount against max stock / recommended qty.
 * Priority: over-max > over-capacity > above-recommended > below-recommended.
 */
export function getActualOrderAmountTone(item) {
    const actual = toNumber(item?.actualOrderAmount);
    if (actual == null) {
        return null;
    }
    const maxStock = toNumber(item?.maxStock);
    const currentStock = toNumber(item?.currentStock);
    const recommended = toNumber(item?.recommendedOrderAmount);

    if (maxStock != null && actual > maxStock) {
        return 'over-max';
    }
    if (maxStock != null && currentStock != null && (currentStock + actual) > maxStock) {
        return 'over-capacity';
    }
    if (recommended != null && actual > recommended) {
        return 'above-recommended';
    }
    if (recommended != null && actual < recommended) {
        return 'below-recommended';
    }
    return null;
}

export function getActualOrderAmountHighlightColor(item) {
    const tone = getActualOrderAmountTone(item);
    if (tone === 'over-max') {
        return '#e81123';
    }
    if (tone === 'over-capacity') {
        return '#FFCC00';
    }
    if (tone === 'above-recommended') {
        return '#f59e0b';
    }
    if (tone === 'below-recommended') {
        return '#3b82f6';
    }
    return null;
}

export const ORDER_UOM_CASE = 'CASE';
export const ORDER_UOM_SINGLE = 'SINGLE';

/** Normalize to SINGLE (default, order qty = total pieces) or CASE (order × box qty). */
export function normalizeOrderUnitOfMeasure(value) {
    const raw = value != null ? String(value).trim().toUpperCase() : '';
    if (raw === ORDER_UOM_CASE) {
        return ORDER_UOM_CASE;
    }
    return ORDER_UOM_SINGLE;
}

export function isOrderUnitSingle(value) {
    return normalizeOrderUnitOfMeasure(value) === ORDER_UOM_SINGLE;
}

export function calculateOrderAmountFromBoxes(boxCount, unitsPerBox) {
    const boxes = toNumber(boxCount);
    const perBox = toNumber(unitsPerBox);
    if (boxes == null || perBox == null) {
        return null;
    }
    return boxes * perBox;
}

/**
 * Total pieces for an order line.
 * CASE: orderQty × boxQty  (e.g. 5 cases × 6 per case = 30)
 * SINGLE: orderQty only     (e.g. order 5 → 5 total; box qty ignored)
 */
export function calculateActualOrderAmount(orderQty, boxQty, orderUnitOfMeasure) {
    if (isOrderUnitSingle(orderUnitOfMeasure)) {
        return toNumber(orderQty);
    }
    return calculateOrderAmountFromBoxes(orderQty, boxQty);
}

/** Display label for order UOM (Single / Case). */
export function formatOrderUomLabel(value) {
    return normalizeOrderUnitOfMeasure(value) === ORDER_UOM_CASE ? 'Case' : 'Single';
}

/**
 * User-facing labels for packing fields.
 * mode: 'order' (PR/PO) | 'delivery' | 'return'
 * Avoids "Box Qty / Order Qty" ambiguity.
 */
export function getOrderPackingLabels(orderUnitOfMeasure, mode = 'order') {
    const single = isOrderUnitSingle(orderUnitOfMeasure);
    if (mode === 'delivery') {
        return {
            unitsPerBox: 'Box Qty',
            orderQty: single ? 'Pieces' : 'Boxes',
            total: 'Total Qty',
            uom: 'Pack as',
            totalHint: single ? '= Pieces' : '= Boxes × Box Qty'
        };
    }
    if (mode === 'return') {
        return {
            unitsPerBox: 'Box Qty',
            orderQty: single ? 'Pieces' : 'Boxes',
            total: 'Return Qty',
            uom: 'Pack as',
            totalHint: single ? '= Pieces' : '= Boxes × Box Qty'
        };
    }
    return {
        unitsPerBox: 'Box Qty',
        orderQty: single ? 'Pieces' : 'Cases',
        total: 'Total Pieces',
        uom: 'Pack as',
        totalHint: single ? '= Pieces' : '= Cases × Box Qty'
    };
}

/** List cell: quantity + Case/Single (e.g. "30 Case"). */
export function formatOrderAmountWithUnit(amount, orderUnitOfMeasure) {
    if (amount == null || amount === '') {
        return '—';
    }
    const unit = isOrderUnitSingle(orderUnitOfMeasure) ? 'Single' : 'Case';
    return `${amount} ${unit}`;
}

/**
 * Product packing size for order/DN/return lines.
 * 0 / null / empty → default 1 and editable.
 * Otherwise use value and lock (must not change).
 */
export function resolveProductBoxSize(rawBoxSize) {
    const n = Number(rawBoxSize);
    if (rawBoxSize == null || String(rawBoxSize).trim() === '' || Number.isNaN(n) || n <= 0) {
        return {boxSize: 1, boxSizeLocked: false};
    }
    return {boxSize: n, boxSizeLocked: true};
}

export function deriveOrderBoxCount(actualOrderAmount, unitsPerBox) {
    const actual = toNumber(actualOrderAmount);
    const perBox = toNumber(unitsPerBox);
    if (actual == null || perBox == null || perBox === 0) {
        return null;
    }
    const boxes = actual / perBox;
    if (!Number.isFinite(boxes) || boxes < 0) {
        return null;
    }
    // Whole boxes only; ceil so derived qty covers recommended/actual need.
    return Math.ceil(boxes);
}
